import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  FileText,
  Wallet,
  Clock,
  CheckCircle,
  XCircle,
  ChartLineUp,
  Plus,
  ArrowRight,
  UsersThree,
  Buildings,
  Briefcase,
  TrendUp,
  TrendDown,
  CurrencyInr,
  Target,
  Trophy,
  CaretDown,
  CaretUp,
  Warning,
  Crown,
} from '@phosphor-icons/react';
import { fmtINR, fmtUSD } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'INR') return `₹${(amount || 0).toLocaleString('en-IN')}`;
  return `$${(amount || 0).toLocaleString('en-US')}`;
};

const USD_TO_INR = 83.5;
const formatDual = (amountINR) => {
  const inr = amountINR || 0;
  const usd = Math.round(inr / USD_TO_INR);
  return { inr: fmtINR(inr), usd: fmtUSD(usd), both: `${fmtINR(inr)} (${fmtUSD(usd)})` };
};
const formatDualUSD = (amountUSD) => {
  const usd = amountUSD || 0;
  const inr = Math.round(usd * USD_TO_INR);
  return { inr: fmtINR(inr), usd: fmtUSD(usd), both: fmtUSD(usd) };
};

const getStatusBadge = (status) => {
  const styles = {
    draft: 'status-draft', submitted: 'status-submitted', reviewing: 'status-reviewing',
    approved: 'status-approved', rejected: 'status-rejected', backed_out: 'bg-red-100 text-red-700',
  };
  return <Badge className={styles[status] || 'status-draft'}>{status?.replace(/_/g, ' ').toUpperCase()}</Badge>;
};

const DashboardHome = () => {
  const { user, isRecruiter, isLead, isManager, isSuperadmin, isCeo } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [teamMonthly, setTeamMonthly] = useState(null);
  const [myDashboard, setMyDashboard] = useState(null);
  const [noPlacementData, setNoPlacementData] = useState(null);
  const [myEarnings, setMyEarnings] = useState(null);
  const [quarterlyPerf, setQuarterlyPerf] = useState(null);
  const [qBonus, setQBonus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportingTo, setReportingTo] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const applyDashboardPayload = (data) => {
    if (!data) return false;
    setStats(data.stats);
    setAnalytics(data.analytics || null);
    setReportingTo(data.reporting_managers || []);
    setMyEarnings(data.my_earnings || null);
    setMyDashboard(data.my_dashboard || null);
    setTeamMonthly(data.team_monthly || null);
    setNoPlacementData(data.no_placement || null);
    setQuarterlyPerf(data.quarterly_performance || null);
    return true;
  };

  const fetchDashboardFallback = async () => {
    const calls = [
      axios.get(`${API_URL}/api/dashboard/stats?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/dashboard/analytics?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }).catch(() => ({ data: null })),
    ];
    if (isRecruiter) {
      calls.push(axios.get(`${API_URL}/api/my-dashboard-data?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }).catch(() => ({ data: null })));
    }
    if (isLead || isManager || isSuperadmin || isCeo) {
      calls.push(axios.get(`${API_URL}/api/team-monthly-data?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }).catch(() => ({ data: null })));
    }
    if (isSuperadmin || isCeo) {
      calls.push(axios.get(`${API_URL}/api/admin/no-placement-entities?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }).catch(() => ({ data: null })));
      calls.push(axios.get(`${API_URL}/api/dashboard/quarterly-performance?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }).catch(() => ({ data: null })));
    }
    calls.push(axios.get(`${API_URL}/api/my-earnings`, { withCredentials: true }).catch(() => ({ data: null })));

    const results = await Promise.all(calls);
    setStats(results[0].data);
    setAnalytics(results[1].data);

    let idx = 2;
    if (isRecruiter) {
      setMyDashboard(results[idx]?.data);
      idx += 1;
    } else if (isLead || isManager || isSuperadmin || isCeo) {
      setTeamMonthly(results[idx]?.data);
      idx += 1;
    }
    if (isSuperadmin || isCeo) {
      setNoPlacementData(results[idx]?.data);
      setQuarterlyPerf(results[idx + 1]?.data);
      idx += 2;
    }
    setMyEarnings(results[results.length - 1]?.data);

    try {
      const rmRes = await axios.get(`${API_URL}/api/reporting-structure`, { withCredentials: true });
      const me = (rmRes.data || []).find(u => u.id === user?.id);
      setReportingTo(me?.reporting_manager_details || []);
    } catch (_) {}
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const homeRes = await axios.get(
        `${API_URL}/api/dashboard/home?date_from=${dateFrom}&date_to=${dateTo}`,
        { withCredentials: true }
      ).catch(() => null);

      if (!homeRes?.data || !applyDashboardPayload(homeRes.data)) {
        await fetchDashboardFallback();
      }

      if (isLead || isManager) {
        try {
          const now = new Date();
          const qNum = Math.floor(now.getMonth() / 3) + 1;
          const qb = await axios.get(
            `${API_URL}/api/admin/quarterly-bonus-summary?quarter=Q${qNum}-${now.getFullYear()}`,
            { withCredentials: true }
          );
          setQBonus(qb.data);
        } catch (_) { /* non-critical */ }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-slate-200"><CardContent className="p-6"><div className="skeleton h-4 w-24 mb-2 rounded" /><div className="skeleton h-8 w-32 rounded" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  const isAdminLike = isSuperadmin || isCeo || isManager || isLead;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-500 mt-1">{stats?.current_quarter} &bull; Here's your performance overview</p>
          {user?.work_location && (
            <span className="text-xs text-slate-400 inline-flex items-center gap-1 mt-0.5">Location: <span className="font-medium text-slate-600">{user.work_location}</span></span>
          )}
          {reportingTo.length > 0 && (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-xs text-slate-400">Reporting to:</span>
              {reportingTo.map(rm => (
                <Badge key={rm.id} variant="outline" className="text-[10px] font-medium">{rm.name}</Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date range filter */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 whitespace-nowrap">From:</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[145px] text-sm" data-testid="dash-date-from" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 whitespace-nowrap">To:</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[145px] text-sm" data-testid="dash-date-to" />
          </div>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white px-4" onClick={fetchData} data-testid="dash-search-btn">
            Search
          </Button>
          {isRecruiter && (
            <Link to="/dashboard/placements/new">
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-placement-btn">
                <Plus size={20} className="mr-2" /> New Placement
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 card-interactive">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Placements</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="stat-total-placements">{stats?.placements?.total || 0}</p>
                <p className="text-[10px] text-slate-400 mt-1">{isRecruiter ? 'Your placements this period' : 'Team placements this period'}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <FileText size={24} className="text-blue-600" weight="duotone" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 card-interactive">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Approved</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1" data-testid="stat-approved">{stats?.placements?.approved || 0}</p>
                <p className="text-[10px] text-slate-400 mt-1">Placements confirmed with incentives finalized</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle size={24} className="text-emerald-600" weight="duotone" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 card-interactive">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">
                  {(isSuperadmin || isCeo) ? 'Pending SA Approval' : 'Pending Review'}
                </p>
                <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="stat-pending">
                  {(isSuperadmin || isCeo)
                    ? (stats?.placements?.manager_approved || 0)
                    : ((stats?.placements?.submitted || 0) + (stats?.placements?.reviewing || 0))}
                </p>
                <p className="text-[10px] text-slate-400 mt-1">
                  {(isSuperadmin || isCeo)
                    ? 'Manager-approved placements awaiting your final approval'
                    : 'Awaiting Lead or Manager review/approval'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
                {(isSuperadmin || isCeo) ? (
                  <Link to="/dashboard/review-queue?filter=pending_sa" className="flex items-center justify-center w-full h-full">
                    <Clock size={24} className="text-amber-600" weight="duotone" />
                  </Link>
                ) : (
                  <Clock size={24} className="text-amber-600" weight="duotone" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 card-interactive">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">{isManager ? 'Pending Review' : 'Quarter Earnings'}</p>
                {isManager ? (
                  <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="stat-pending-review">{(stats?.placements?.submitted || 0) + (stats?.placements?.reviewing || 0)}</p>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-earnings">{formatDual(stats?.earnings?.quarter_earnings).inr}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{formatDual(stats?.earnings?.quarter_earnings).usd}</p>
                  </>
                )}
                <p className="text-[10px] text-slate-400 mt-1">{isManager ? 'Placements needing your approval' : 'Total approved incentives this quarter (INR)'}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                {isManager ? <Clock size={24} className="text-amber-600" weight="duotone" /> : <Wallet size={24} className="text-slate-600" weight="duotone" />}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========== Lead/Manager: Quarterly Bonus Card ========== */}
      {(isLead || isManager) && qBonus && qBonus.teams && qBonus.teams.length > 0 && (
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-white" data-testid="quarterly-bonus-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={20} className="text-amber-600" weight="fill" />
              <p className="font-semibold text-amber-800">Quarterly Bonus — {qBonus.quarter}</p>
            </div>
            <div className="space-y-3">
              {qBonus.teams.map(team => {
                const myInfo = (team.lead?.user_id === user?.id) ? team.lead :
                               (team.manager?.user_id === user?.id) ? team.manager : null;
                if (!myInfo) return null;
                return (
                  <div key={team.team_name} className={`p-3 rounded-lg border ${team.eligible ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{team.team_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Achievement: <strong>{team.achieved}/{team.quarterly_target}</strong> ({team.achievement_pct}%)
                          {team.multiplier > 0 && <> — Multiplier: <strong>{team.multiplier}x</strong></>}
                        </p>
                        {team.eligible && (
                          <p className="text-[10px] text-emerald-600 mt-1">
                            Eng: {myInfo.he_count} × {myInfo.multiplier} × {formatCurrency(myInfo.he_amount)} = {formatCurrency(myInfo.he_bonus)}
                            {myInfo.le_count > 0 && <> | LE: {myInfo.le_count} × {myInfo.multiplier} × {formatCurrency(myInfo.le_amount)} = {formatCurrency(myInfo.le_bonus)}</>}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        {team.eligible ? (
                          <>
                            <p className="text-xl font-bold text-emerald-700">{formatCurrency(myInfo.total_bonus)}</p>
                            <Badge className={myInfo.status === 'confirmed' ? 'bg-emerald-200 text-emerald-800 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>
                              {myInfo.status === 'confirmed' ? 'CONFIRMED' : 'ELIGIBLE'}
                            </Badge>
                          </>
                        ) : (
                          <p className="text-sm text-red-600 font-medium">Below 90%</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========== SA/CEO: Quarterly Performance ========== */}
      {(isSuperadmin || isCeo) && quarterlyPerf && (
        <div className="space-y-4" data-testid="quarterly-performance">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 font-['Outfit']">Quarterly Performance</h2>
              <p className="text-xs text-slate-500 mt-0.5">Quarter-over-quarter metrics with MTD snapshot</p>
            </div>
          </div>

          {/* MTD Card */}
          {quarterlyPerf.mtd && (
            <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-white" data-testid="mtd-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><Target size={16} className="text-blue-600" /></div>
                    <span className="text-sm font-semibold text-blue-800">{quarterlyPerf.mtd.label}</span>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 text-[10px]">Month-to-Date</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div><p className="text-[10px] text-slate-400 uppercase">Total</p><p className="text-xl font-bold text-slate-900">{quarterlyPerf.mtd.total}</p></div>
                  <div><p className="text-[10px] text-slate-400 uppercase">Approved</p><p className="text-xl font-bold text-emerald-700">{quarterlyPerf.mtd.approved}</p></div>
                  <div><p className="text-[10px] text-slate-400 uppercase">Pending</p><p className="text-xl font-bold text-amber-600">{quarterlyPerf.mtd.pending}</p></div>
                  <div><p className="text-[10px] text-slate-400 uppercase">Backouts</p><p className="text-xl font-bold text-red-600">{quarterlyPerf.mtd.backouts}</p></div>
                  <div><p className="text-[10px] text-slate-400 uppercase">Incentive</p><p className="text-xl font-bold text-blue-700">{fmtINR(quarterlyPerf.mtd.total_incentive)}</p><p className="text-[10px] text-slate-400">{fmtUSD(Math.round((quarterlyPerf.mtd.total_incentive || 0) / 83.5))}</p></div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quarter Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quarterlyPerf.quarters?.map((q) => (
              <Card key={q.label} className={`border-slate-200 ${q.is_current ? 'ring-2 ring-blue-200' : ''}`} data-testid={`quarter-card-${q.label}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-slate-800">{q.label}</span>
                    {q.is_current && <Badge className="bg-blue-50 text-blue-600 text-[9px]">Current</Badge>}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Placements</span>
                      <span className="font-semibold text-slate-800">{q.total}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Approved</span>
                      <span className="font-semibold text-emerald-700">{q.approved}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Rejected</span>
                      <span className="font-semibold text-red-600">{q.rejected}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Backouts</span>
                      <span className="font-semibold text-orange-600">{q.backouts}</span>
                    </div>
                    <div className="pt-2 border-t border-slate-100">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Sales</span>
                        <span className="font-medium">{q.sales?.approved}/{q.sales?.total}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Recruiting</span>
                        <span className="font-medium">{q.recruiting?.approved}/{q.recruiting?.total}</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-[10px] text-slate-400 uppercase">Total Incentive</p>
                      <p className="text-base font-bold text-slate-900">{fmtINR(q.total_incentive)}</p>
                      <p className="text-[10px] text-slate-400">{fmtUSD(Math.round((q.total_incentive || 0) / 83.5))}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quarter-over-Quarter Comparison */}
          {quarterlyPerf.quarters?.length >= 2 && (() => {
            const current = quarterlyPerf.quarters.find(q => q.is_current);
            const previous = quarterlyPerf.quarters.find(q => !q.is_current);
            if (!current || !previous) return null;
            const placeDiff = current.total - previous.total;
            const approvedDiff = current.approved - previous.approved;
            const backoutDiff = current.backouts - previous.backouts;
            const incentiveDiff = (current.total_incentive || 0) - (previous.total_incentive || 0);
            const pctChange = previous.total > 0 ? Math.round(((current.total - previous.total) / previous.total) * 100) : 0;
            const approvalRate = current.total > 0 ? Math.round((current.approved / current.total) * 100) : 0;
            const prevApprovalRate = previous.total > 0 ? Math.round((previous.approved / previous.total) * 100) : 0;
            return (
              <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-white" data-testid="quarter-comparison">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendUp size={18} className="text-slate-600" />
                    <span className="text-sm font-semibold text-slate-800">{current.label} vs {previous.label} — Quarter Comparison</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Placements</p>
                      <p className="text-lg font-bold text-slate-900">{current.total} <span className={`text-sm ${placeDiff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{placeDiff >= 0 ? '+' : ''}{placeDiff}</span></p>
                      <p className="text-[10px] text-slate-400">was {previous.total} ({pctChange >= 0 ? '+' : ''}{pctChange}%)</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Approved</p>
                      <p className="text-lg font-bold text-emerald-700">{current.approved} <span className={`text-sm ${approvedDiff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{approvedDiff >= 0 ? '+' : ''}{approvedDiff}</span></p>
                      <p className="text-[10px] text-slate-400">was {previous.approved}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Approval Rate</p>
                      <p className="text-lg font-bold text-blue-700">{approvalRate}%</p>
                      <p className="text-[10px] text-slate-400">was {prevApprovalRate}%</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Backouts</p>
                      <p className="text-lg font-bold text-red-600">{current.backouts} <span className={`text-sm ${backoutDiff <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{backoutDiff >= 0 ? '+' : ''}{backoutDiff}</span></p>
                      <p className="text-[10px] text-slate-400">was {previous.backouts}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Incentive</p>
                      <p className="text-lg font-bold text-slate-900">{fmtINR(current.total_incentive)}</p>
                      <p className="text-[10px] text-slate-400">{fmtUSD(Math.round((current.total_incentive || 0) / 83.5))}</p>
                      <p className={`text-[10px] ${incentiveDiff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{incentiveDiff >= 0 ? '+' : ''}{fmtINR(incentiveDiff)} ({fmtUSD(Math.round(incentiveDiff / 83.5))}) vs {previous.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      )}

      {/* ========== RECRUITER: Individual Monthly Data ========== */}
      {isRecruiter && myDashboard && (
        <RecruiterMonthlyView data={myDashboard} month={selectedMonth} year={selectedYear} />
      )}

      {/* ========== LEAD / MANAGER / ADMIN: Team Monthly Data ========== */}
      {isAdminLike && teamMonthly && (
        <TeamMonthlyView data={teamMonthly} hideAmounts={teamMonthly.hide_amounts} isManager={isManager} />
      )}

      {/* ========== No-Placement Entity Tracker (Superadmin only) ========== */}
      {(isSuperadmin || isCeo) && noPlacementData && (
        <NoPlacementTracker data={noPlacementData} dateFrom={dateFrom} dateTo={dateTo} />
      )}

      {/* Analytics Section - Only for admin-like roles */}
      {isAdminLike && analytics && (
        <>
          <div>
            <h2 className="text-lg font-semibold text-slate-800 font-['Outfit']">Analytics Overview</h2>
            <p className="text-xs text-slate-500 mt-0.5">Module breakdown and top vendors/clients.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center"><Briefcase size={20} className="text-indigo-600" /></div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wide">Sales Teams</p><p className="text-xl font-bold text-slate-900">{analytics.module_breakdown?.sales?.total || 0}</p></div>
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{analytics.module_breakdown?.sales?.approved || 0} approved</span>
                  {!isManager && !isLead && !isRecruiter && <span className="text-sm font-semibold text-indigo-700">{formatDual(analytics.module_breakdown?.sales?.incentive).both}</span>}
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center"><UsersThree size={20} className="text-teal-600" /></div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wide">Recruiting Teams</p><p className="text-xl font-bold text-slate-900">{analytics.module_breakdown?.recruiting?.total || 0}</p></div>
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{analytics.module_breakdown?.recruiting?.approved || 0} approved</span>
                  {!isManager && !isLead && !isRecruiter && <span className="font-semibold text-teal-700">{formatCurrency(analytics.module_breakdown?.recruiting?.incentive)}</span>}
                </div>
              </CardContent>
            </Card>
            {(isSuperadmin || isCeo) && (
              <Card className={`border-slate-200 ${(analytics.total_team_incentive || 0) >= (analytics.total_team_budget || 0) ? 'bg-emerald-50/50' : 'bg-red-50/50'}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><CurrencyInr size={20} className="text-amber-600" /></div>
                    <div><p className="text-xs text-slate-500 uppercase tracking-wide">Budget vs Incentive</p><p className="text-lg font-bold text-slate-900">{formatDual(analytics.total_team_budget).inr}</p><p className="text-[10px] text-slate-400">{formatDual(analytics.total_team_budget).usd}</p></div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Incentive: {formatDual(analytics.total_team_incentive).both}</span>
                    <span className={`font-semibold flex items-center gap-1 ${(analytics.total_team_incentive || 0) >= (analytics.total_team_budget || 0) ? 'text-emerald-700' : 'text-red-700'}`}>
                      {(analytics.total_team_incentive || 0) >= (analytics.total_team_budget || 0) ? <TrendUp size={14} /> : <TrendDown size={14} />}
                      {formatCurrency(Math.abs((analytics.total_team_incentive || 0) - (analytics.total_team_budget || 0)))}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Top Vendors & Clients */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {analytics.top_vendors?.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-2"><CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><Buildings size={20} className="text-slate-500" /> Top Vendors</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics.top_vendors.map((v, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`vendor-row-${i}`}>
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          <div><p className="text-sm font-medium text-slate-800">{v.name}</p><p className="text-[10px] text-slate-400">{v.approved} approved of {v.count}</p></div>
                        </div>
                        {(isSuperadmin || isCeo) && (
                          <div className="text-right">
                            <span className="text-sm font-semibold text-emerald-700">{formatDual(v.incentive).both}</span>
                            <p className="text-[10px] text-slate-400">{v.count} placements</p>
                          </div>
                        )}
                        {!(isSuperadmin || isCeo) && <span className="text-sm font-semibold text-blue-700">{v.count} placements</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {analytics.top_clients?.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-2"><CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><Briefcase size={20} className="text-slate-500" /> Top Clients</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics.top_clients.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`client-row-${i}`}>
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          <div><p className="text-sm font-medium text-slate-800">{c.name}</p><p className="text-[10px] text-slate-400">{c.approved} approved of {c.count}</p></div>
                        </div>
                        {(isSuperadmin || isCeo) && (
                          <div className="text-right">
                            <span className="text-sm font-semibold text-emerald-700">{formatDual(c.incentive).both}</span>
                            <p className="text-[10px] text-slate-400">{c.count} placements</p>
                          </div>
                        )}
                        {!(isSuperadmin || isCeo) && <span className="text-sm font-semibold text-blue-700">{c.count} placements</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Earnings + Recent Placements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {!isManager && (
          <Card className="border-slate-200 lg:col-span-1" data-testid="dashboard-earnings-card">
            <CardHeader className="pb-2"><CardTitle className="text-lg font-['Outfit']">My Earnings</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Total Incentive Earnings</p>
                    <p className="text-2xl font-bold text-emerald-700" data-testid="dashboard-total-earnings">{fmtINR(myEarnings?.total_earnings || 0)}</p>
                    <p className="text-xs text-slate-400">{fmtUSD(Math.round((myEarnings?.total_earnings || 0) / 83.5))}</p>
                  </div>
                  <Wallet size={24} className="text-emerald-500" />
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Placements Contributing</p>
                    <p className="text-xl font-bold text-blue-700" data-testid="dashboard-earning-placements">{myEarnings?.count || 0}</p>
                  </div>
                  <Briefcase size={24} className="text-blue-500" />
                </div>
                {myEarnings?.placements?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400 uppercase font-semibold">Recent</p>
                    {myEarnings.placements.slice(0, 3).map((p) => (
                      <Link key={p.placement_id} to={`/dashboard/placements/${p.placement_id}`} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`recent-earning-${p.placement_id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800 truncate">{p.candidate_name}</p>
                          <p className="text-[10px] text-slate-400">{p.roles?.map(r => r.role).join(', ')}</p>
                        </div>
                        <span className="text-sm font-bold text-emerald-700 ml-3">{fmtINR(p.total_share)} <span className="text-[10px] text-slate-400">({fmtUSD(Math.round((p.total_share || 0) / 83.5))})</span></span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/dashboard/earnings"><Button variant="ghost" className="w-full mt-4 text-blue-600 hover:text-blue-700" data-testid="view-earnings-btn">View All Earnings <ArrowRight size={16} className="ml-2" /></Button></Link>
            </CardContent>
          </Card>
        )}
        <Card className={`border-slate-200 ${isManager ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-['Outfit']">Recent Placements</CardTitle>
              <Link to="/dashboard/placements"><Button variant="ghost" size="sm" className="text-blue-600">View All</Button></Link>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_placements?.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText size={48} className="mx-auto mb-2 opacity-50" /><p>No placements yet</p>
                {isRecruiter && (<Link to="/dashboard/placements/new"><Button variant="outline" className="mt-4">Create Your First Placement</Button></Link>)}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead><tr>
                    <th className="px-4 py-3">Candidate</th><th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Placed By</th><th className="px-4 py-3">Margin</th><th className="px-4 py-3">Status</th>
                    {!isManager && <th className="px-4 py-3 text-right">Payout</th>}
                  </tr></thead>
                  <tbody>
                    {stats?.recent_placements?.map((placement) => (
                      <tr key={placement.id}>
                        <td className="font-medium">{placement.candidate_name}</td>
                        <td className="text-slate-500">{placement.category?.replace(/_/g, ' ').toUpperCase()}</td>
                        <td className="text-slate-600 text-xs">{placement.created_by_name || '-'}</td>
                        <td>{fmtUSD(placement.margin)}/hr</td>
                        <td>{getStatusBadge(placement.status)}</td>
                        {!isManager && <td className="text-right font-medium">{fmtINR(placement.my_projected_payout ?? placement.my_split_amount ?? placement.incentives?.my_split ?? placement.incentives?.total_placement ?? 0)} <span className="text-[9px] text-slate-400">({fmtUSD(Math.round((placement.my_projected_payout ?? placement.my_split_amount ?? placement.incentives?.my_split ?? placement.incentives?.total_placement ?? 0) / 83.5))})</span></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-lg font-['Outfit']">Placement Status Breakdown</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Draft', count: stats?.placements?.draft, color: 'slate' },
              { label: 'Submitted', count: stats?.placements?.submitted, color: 'blue' },
              { label: 'Reviewing', count: stats?.placements?.reviewing, color: 'amber' },
              { label: 'Approved', count: stats?.placements?.approved, color: 'emerald' },
              { label: 'Rejected', count: stats?.placements?.rejected, color: 'red' },
            ].map(s => (
              <div key={s.label} className={`p-4 rounded-lg bg-${s.color}-50 border border-${s.color}-200 text-center`}>
                <p className={`text-2xl font-bold text-${s.color}-600`}>{s.count || 0}</p>
                <p className={`text-sm text-${s.color}-600 mt-1`}>{s.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ===== Recruiter Monthly View ===== */
const RecruiterMonthlyView = ({ data, month, year }) => {
  if (!data || data.view_type !== 'individual') return null;
  const targetPct = data.my_target > 0 ? Math.round((data.approved / data.my_target) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="recruiter-monthly-view">
      <h2 className="text-lg font-semibold text-slate-800 font-['Outfit']">
        My Monthly Performance — {MONTHS[month - 1]} {year}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-slate-200"><CardContent className="p-4 text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Total</p>
          <p className="text-2xl font-bold text-slate-800">{data.total_placements}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Approved</p>
          <p className="text-2xl font-bold text-emerald-600">{data.approved}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Pending</p>
          <p className="text-2xl font-bold text-amber-600">{data.pending}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Backouts</p>
          <p className="text-2xl font-bold text-red-600">{data.backouts}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 text-center">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Incentive</p>
          <p className="text-xl font-bold text-emerald-700">{fmtINR(data.total_incentive || 0)}</p>
          <p className="text-[10px] text-slate-400">{fmtUSD(Math.round((data.total_incentive || 0) / 83.5))}</p>
        </CardContent></Card>
      </div>
      {/* Target Progress */}
      {data.my_target > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target size={18} className="text-blue-600" />
                <span className="text-sm font-medium text-slate-700">My Target: {data.approved} / {data.my_target}</span>
              </div>
              <Badge className={targetPct >= 100 ? 'bg-emerald-100 text-emerald-700' : targetPct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>{targetPct}%</Badge>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5">
              <div className={`h-full rounded-full transition-all ${targetPct >= 100 ? 'bg-emerald-500' : targetPct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(targetPct, 100)}%` }} />
            </div>
          </CardContent>
        </Card>
      )}
      {/* Quarter progress */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-amber-600" />
            <span className="text-sm text-slate-600">Quarter ({data.quarter}) Approved: <strong className="text-slate-800">{data.quarter_approved}</strong></span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ===== Lead/Manager/Admin Team Monthly View ===== */
const TeamMonthlyView = ({ data, hideAmounts, isManager }) => {
  const [expandedTeams, setExpandedTeams] = useState({});
  if (!data?.teams?.length) return null;

  const toggleExpand = (id) => setExpandedTeams(p => ({ ...p, [id]: !p[id] }));

  const getRoleLabel = (role) => {
    const map = { sales_recruiter: 'Recruiter', sales_technical_recruiter: 'Recruiter', sales_lead: 'Lead', sales_manager: 'Manager', sales_assistant_manager: 'Asst. Mgr', technical_recruiter: 'Recruiter', recruiting_lead: 'Lead', recruiting_manager: 'Manager', recruiting_assistant_manager: 'Asst. Mgr' };
    return map[role] || role?.replace(/_/g, ' ');
  };

  return (
    <div className="space-y-4" data-testid="team-monthly-view">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800 font-['Outfit']">
          Team Data — {data.date_from && data.date_to
            ? `${new Date(data.date_from).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(data.date_to).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            : `${MONTHS[(data.month || 1) - 1]} ${data.year}`} ({data.quarter})
        </h2>
        {hideAmounts && <Badge variant="outline" className="text-xs text-slate-500">Amounts hidden for managers</Badge>}
      </div>

      {/* Group by Manager */}
      {(() => {
        const mgrGroups = {};
        for (const team of data.teams.filter(t => !t.name?.toLowerCase().includes('direct'))) {
          const mgrName = team.manager_name || 'Unassigned';
          if (!mgrGroups[mgrName]) mgrGroups[mgrName] = { manager_name: mgrName, manager_id: team.manager_id, teams: [] };
          mgrGroups[mgrName].teams.push(team);
        }
        return Object.values(mgrGroups).map(group => (
          <div key={group.manager_name} className="space-y-3">
            {/* Manager Header */}
            <div className="flex items-center gap-3 bg-gradient-to-r from-slate-900 to-slate-800 text-white px-5 py-2.5 rounded-xl">
              <Crown size={16} className="text-amber-400" />
              <span className="font-semibold text-sm">{group.manager_name}</span>
              <span className="text-[10px] text-slate-400 uppercase">{group.teams.length} team{group.teams.length !== 1 ? 's' : ''}</span>
            </div>

            {group.teams.map((team) => {
              const isExpanded = expandedTeams[team.id];
              // Targets are monthly — multiply by 3 for quarterly display
              const qCompanyTarget = (team.company_target || 0) * 3;
              const qTeamTarget = (team.team_given_target || 0) * 3;
              const givenPct = qTeamTarget > 0 ? Math.round((team.reached_target / qTeamTarget) * 100) : 0;
              const companyPct = qCompanyTarget > 0 ? Math.round((team.reached_target / qCompanyTarget) * 100) : 0;

              return (
          <Card key={team.id} className="border-slate-200 overflow-hidden" data-testid={`team-monthly-${team.id}`}>
            {/* Team Header */}
            <div className="px-5 py-3 cursor-pointer hover:bg-slate-50 transition-colors flex items-center gap-4" onClick={() => toggleExpand(team.id)}>
              <button className="text-slate-400 shrink-0">{isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}</button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{team.name}</span>
                  <Badge className={`text-[10px] ${team.department === 'sales' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>{team.department}</Badge>
                </div>
                <div className="flex gap-4 text-[11px] text-slate-500 mt-0.5">
                  {team.lead_name && <span>Lead: <strong className="text-slate-700">{team.lead_name}</strong></span>}
                  <span>{team.member_count} members</span>
                </div>
              </div>
              {/* Key metrics inline */}
              <div className="hidden lg:flex items-center gap-5 text-xs shrink-0">
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Placements</p>
                  <p className="font-bold text-blue-700">{team.month_total}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Approved</p>
                  <p className="font-bold text-emerald-700">{team.month_approved}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Team Target</p>
                  <p className={`font-semibold ${givenPct >= 100 ? 'text-emerald-700' : 'text-slate-700'}`}>
                    {qTeamTarget > 0 ? `${team.reached_target}/${qTeamTarget}` : 'Not set'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Company Target</p>
                  <p className={`font-semibold ${companyPct >= 100 ? 'text-emerald-700' : 'text-slate-700'}`}>
                    {qCompanyTarget > 0 ? `${team.reached_target}/${qCompanyTarget}` : 'Not set'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Backouts</p>
                  <p className={`font-semibold ${team.month_backouts > 0 ? 'text-red-600' : 'text-slate-500'}`}>{team.month_backouts}</p>
                </div>
                {!hideAmounts && (
                  <div className="text-center">
                    <p className="text-[10px] text-slate-400 uppercase">Incentive</p>
                    <p className="font-bold text-emerald-700 text-[11px]">{formatDual(team.month_incentive || 0).both}</p>
                  </div>
                )}
                {/* Quarterly Bonus Eligibility */}
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Q-Bonus</p>
                  {team.bonus_eligible ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Eligible</Badge>
                  ) : (
                    <span className="text-[10px] text-slate-400">{team.bonus_achievement_pct}%</span>
                  )}
                </div>
              </div>
            </div>

            {/* Expanded: Targets + Members */}
            {isExpanded && (
              <div className="bg-slate-50/70 border-t border-slate-100">
                {/* Target Progress */}
                <div className="px-6 py-3 border-b border-slate-100 space-y-2">
                  {qTeamTarget > 0 && (
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-slate-500 w-28 shrink-0">Team Target</span>
                      <div className="flex-1 bg-slate-200 rounded-full h-2.5 overflow-hidden">
                        <div className={`h-full rounded-full ${givenPct >= 100 ? 'bg-emerald-500' : givenPct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(givenPct, 100)}%` }} />
                      </div>
                      <span className={`text-xs font-semibold w-16 text-right ${givenPct >= 100 ? 'text-emerald-700' : 'text-slate-700'}`}>{team.reached_target}/{qTeamTarget} ({givenPct}%)</span>
                    </div>
                  )}
                  {qCompanyTarget > 0 && (
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-slate-500 w-28 shrink-0">Company Target</span>
                      <div className="flex-1 bg-slate-200 rounded-full h-2.5 overflow-hidden">
                        <div className={`h-full rounded-full ${companyPct >= 100 ? 'bg-emerald-500' : companyPct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(companyPct, 100)}%` }} />
                      </div>
                      <span className={`text-xs font-semibold w-16 text-right ${companyPct >= 100 ? 'text-emerald-700' : 'text-slate-700'}`}>{team.reached_target}/{qCompanyTarget} ({companyPct}%)</span>
                    </div>
                  )}
                  {/* Quarterly Bonus Status */}
                  <div className="flex items-center gap-3 pt-1">
                    <Trophy size={16} className="text-amber-600" />
                    <span className="text-xs text-slate-600">
                      Quarter ({team.quarter}): {team.quarter_approved} approved / {team.quarter_target > 0 ? `${team.quarter_target} target` : 'No quarterly target'}
                      {team.bonus_eligible ? <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">Bonus Eligible</Badge> : team.quarter_target > 0 ? <span className="ml-2 text-amber-600 font-medium">({team.bonus_achievement_pct}% achieved)</span> : null}
                    </span>
                  </div>
                </div>

                {/* Member Table */}
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider">
                      <th className="px-6 py-1.5 text-left font-semibold">Name</th>
                      <th className="px-3 py-1.5 text-left font-semibold">Role</th>
                      <th className="px-3 py-1.5 text-left font-semibold">Sub Team</th>
                      <th className="px-3 py-1.5 text-center font-semibold">Placements</th>
                      <th className="px-3 py-1.5 text-center font-semibold">Approved</th>
                      <th className="px-3 py-1.5 text-center font-semibold">Backouts</th>
                      <th className="px-3 py-1.5 text-center font-semibold">Q-Approved</th>
                      {!hideAmounts && <th className="px-3 py-1.5 text-right font-semibold">Salary</th>}
                      {!hideAmounts && <th className="px-3 py-1.5 text-right font-semibold">Incentive</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {team.members?.map((m) => (
                      <tr key={m.id} className="hover:bg-white/80" data-testid={`team-member-${m.id}`}>
                        <td className="px-6 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">{m.name?.charAt(0)}</div>
                            <span className="font-medium text-slate-800">{m.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{getRoleLabel(m.role)}</Badge></td>
                        <td className="px-3 py-2 text-slate-500">{m.role?.replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2 text-center font-medium text-blue-700">{m.placements}</td>
                        <td className="px-3 py-2 text-center font-medium text-emerald-600">{m.approved}</td>
                        <td className="px-3 py-2 text-center font-medium text-red-600">{m.backouts}</td>
                        <td className="px-3 py-2 text-center font-medium text-slate-700">{m.quarter_approved}</td>
                        {!hideAmounts && <td className="px-3 py-2 text-right text-slate-600 text-[10px]">{m.salary ? formatDual(m.salary).both : '-'}</td>}
                        {!hideAmounts && <td className="px-3 py-2 text-right font-semibold text-emerald-700 text-[10px]">{formatDual(m.incentive || 0).both}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!team.members || team.members.length === 0) && (
                  <p className="px-6 py-4 text-sm text-slate-400 text-center">No members in this team</p>
                )}
              </div>
            )}
          </Card>
              );
            })}
          </div>
        ));
      })()}
    </div>
  );
};

/* ===== No-Placement Entity Tracker ===== */
const NoPlacementTracker = ({ data, dateFrom, dateTo }) => {
  const orgs = data?.organizations || [];
  const [activeOrg, setActiveOrg] = useState(orgs[0]?.name || '');

  if (!data || orgs.length === 0) return null;

  const section = orgs.find(o => o.name === activeOrg) || orgs[0];

  return (
    <Card className="border-slate-200" data-testid="no-placement-tracker">
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-2">
          <div>
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
              <Warning size={20} className="text-amber-600" /> Entities Without Placements
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Clients and vendors with no placement activity per organization ({dateFrom} to {dateTo})</p>
          </div>
          <div className="flex gap-1 flex-wrap">
            {orgs.map((org) => (
              <Button key={org.name} variant={activeOrg === org.name ? 'default' : 'outline'} size="sm" onClick={() => setActiveOrg(org.name)}
                className={`text-[11px] ${activeOrg === org.name ? 'bg-slate-800' : ''}`}
                data-testid={`org-tab-${org.name}`}>
                {org.name}
                <Badge variant="outline" className="ml-1.5 text-[9px]">{org.active_vendor_count + org.active_client_count} active</Badge>
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Module breakdown */}
        {section?.modules && (
          <div className="flex gap-3 mb-4">
            <Badge variant="outline" className="text-[10px] gap-1">Sales: <strong>{section.modules.sales || 0}</strong></Badge>
            <Badge variant="outline" className="text-[10px] gap-1">Recruiting: <strong>{section.modules.recruiting || 0}</strong></Badge>
            <Badge variant="outline" className="text-[10px] gap-1">Bench Sales: <strong>{section.modules.bench_sales || 0}</strong></Badge>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Inactive Vendors */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Vendors not worked with by {section.name} ({(section?.inactive_vendors || []).length})
            </h4>
            {(section?.inactive_vendors || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">All vendors have active placements</p>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0"><tr>
                    <th className="px-3 py-1.5 text-left font-semibold text-slate-500">Vendor</th>
                    <th className="px-3 py-1.5 text-center font-semibold text-slate-500">Days Inactive</th>
                    <th className="px-3 py-1.5 text-right font-semibold text-slate-500">Last Placement</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {section.inactive_vendors.map((v, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 font-medium text-slate-800">{v.name}</td>
                        <td className="px-3 py-1.5 text-center"><Badge className={v.days_inactive > 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} variant="outline">{v.days_inactive}d</Badge></td>
                        <td className="px-3 py-1.5 text-right text-slate-500">{v.last_placement || 'Never'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* Inactive Clients */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Clients not worked with by {section.name} ({(section?.inactive_clients || []).length})
            </h4>
            {(section?.inactive_clients || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">All clients have active placements</p>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0"><tr>
                    <th className="px-3 py-1.5 text-left font-semibold text-slate-500">Client</th>
                    <th className="px-3 py-1.5 text-center font-semibold text-slate-500">Days Inactive</th>
                    <th className="px-3 py-1.5 text-right font-semibold text-slate-500">Last Placement</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {section.inactive_clients.map((c, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 font-medium text-slate-800">{c.name}</td>
                        <td className="px-3 py-1.5 text-center"><Badge className={c.days_inactive > 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} variant="outline">{c.days_inactive}d</Badge></td>
                        <td className="px-3 py-1.5 text-right text-slate-500">{c.last_placement || 'Never'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardHome;
